import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { deepCopy } from "@utils/toolBox";
import { useDebounceFn } from "@vueuse/core";
import { getClientId } from "@utils/clientId";
import genToken from "@utils/generateToken";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { getTranslationManualBaseFee } from "@utils/pricer/baseFeeCalculators";
import { captureEvent } from "@/lib/analytics";

const getDefaultProject = () => ({
  hs_locale: "fr",
  creation_mode: "custom",
  name: "",
  client: "",
  end_client: "",
  crm_id: "",
  brief: "",
  has_ask_licence: false,
  targets: [],
});

const DESIGN_OPTIONS = [
  { value: "custom", label: "Custom", icon: "file" },
  { value: "ai", label: "AI-powered", custom_icon: "ai-powered.svg" },
];

export const usePricerProjectStore = defineStore("pricerProject", () => {
  // STATE
  const project = ref(getDefaultProject());
  const initialProject = ref(getDefaultProject());
  const selectedTargetToken = ref(null);
  const isPreviewLoading = ref(false);
  const watcherInitialized = ref(false);

  // GETTERS
  const isNewProject = computed(() => project.value?.token == null);
  const designOptions = computed(() => DESIGN_OPTIONS);

  const selectedTarget = computed(() => {
    if (!project.value.targets || !selectedTargetToken.value) return null;
    return project.value.targets.find(
      (t) => t.id === selectedTargetToken.value || t.token === selectedTargetToken.value,
    );
  });

  const hasProjectChanged = computed(() => {
    const replacer = (key, value) => (key.endsWith("_auto") ? undefined : value);
    return JSON.stringify(initialProject.value, replacer) !== JSON.stringify(project.value, replacer);
  });

  const isAiProject = computed(() => project.value.creation_mode === "ai");

  const targetHasMissingParams = computed(() => {
    return project.value.targets.some((target) => !target.name || !target.description);
  });

  const projectHasMissingParams = computed(() => {
    const hasName = !!project.value.name?.trim();
    const hasClient = !!project.value.client?.trim();
    const hasCreationMode = !!project.value.creation_mode;
    const hasTopic = isAiProject.value || !!project.value.survey_topic?.trim();
    const hasBrief = !isAiProject.value || !!project.value.brief?.trim();

    return !hasName || !hasClient || !hasCreationMode || !hasBrief || !hasTopic 
  });

  // ACTIONS
  const setProjectData = (data) => {
    project.value = data;
    if (!project.value.targets) project.value.targets = [];
    initialProject.value = deepCopy(data);
    if (!selectedTargetToken.value || !project.value.targets.find((t) => t?.token === selectedTargetToken.value)) {
      selectedTargetToken.value = project.value.targets[0]?.token;
    }
  };

  const setSelectedTarget = (data) => {
    const newTarget = {
      ...data,
      token: genToken(),
      name: `Target ${project.value.targets.length + 1}`,
      active: true,
      countries: data.countries.map((country) => ({ ...country, token: genToken() })),
    };
    project.value.targets.push(newTarget);
    selectedTargetToken.value = newTarget.token;
  };

  const sendRequest = async (requestPromise, objectToSet = "project") => {
    try {
      const { response, data } = await requestPromise;
      if (response.value.ok) {
        if (objectToSet === "project") setProjectData(data.value);
        else if (objectToSet === "target") setSelectedTarget(data.value);
        else console.error("Invalid objectToSet");
      }
      return response.value.ok;
    } catch (error) {
      handleError(error);
      return false;
    }
  };

  // -- API Calls --
  const fetch = async (projectToken) => {
    await sendRequest(useMyFetch(`/pricer/projects/${projectToken}`).json());
    initPreviewWatcher();
  };

  const createProject = async () => {
    const clientId = getClientId();
    const created = await sendRequest(
      useMyFetch(`/pricer/projects/?client_id=${clientId}`).json().post({ pricing_project: project.value }),
    );

    if (created) captureEvent("pricer_project_created", { project_token: project.value.token });
  };

  const updateProject = async () => {
    const updated = await sendRequest(
      useMyFetch(`/pricer/projects/${project.value.token}`).json().patch({ pricing_project: project.value }),
    );

    if (updated) captureEvent("pricer_project_updated", { project_token: project.value.token });
  };

  const saveProject = async () => {
    isNewProject.value ? await createProject() : await updateProject();
  };

  let skipNextWatch = false;

  const preview = async () => {
    if (isPreviewLoading.value || project.value.has_obsolete_pricing_version) return;

    isPreviewLoading.value = true;
    try {
      const { response, data } = await useMyFetch(`/pricer/projects/${project.value.token}/preview`)
        .json()
        .put({ pricing_project: project.value });

      if (response.value.ok) {
        skipNextWatch = true;
        project.value = data.value;
        if (!project.value.targets) project.value.targets = [];
      }
    } catch (error) {
      handleError(error);
    } finally {
      isPreviewLoading.value = false;
    }
  };

  const debouncedPreview = useDebounceFn(preview, 500);

  // -- Lifecycle --
  const resetProject = () => setProjectData(getDefaultProject());
  const initProject = () => setProjectData(getDefaultProject());

  // -- Target Logic --
  const addTarget = async () => {
    await sendRequest(useMyFetch(`/pricer/projects/${project.value.token}/targets/new`).json(), "target");
    debouncedPreview();
  };

  const removeTarget = (index) => {
    const targetToDelete = project.value.targets[index];
    project.value.targets.splice(index, 1);
    if (selectedTargetToken.value === targetToDelete.token) {
      const nextTarget = project.value.targets[index] || project.value.targets[index - 1] || project.value.targets[0];
      selectedTargetToken.value = nextTarget ? nextTarget.token : null;
    }
    debouncedPreview();
  };

  const duplicateTarget = (index) => {
    const targetToDuplicate = project.value.targets[index];
    const newTarget = { ...deepCopy(targetToDuplicate), token: genToken(), name: `${targetToDuplicate.name} (Copy)` };
    const DELETE_KEYS = ["id", "created_at", "updated_at"];

    DELETE_KEYS.forEach((key) => delete newTarget[key]);

    if (newTarget.countries) {
      newTarget.countries.forEach((country) => {
        DELETE_KEYS.forEach((key) => delete country[key]);
        country.token = genToken();
      });
    }
    project.value.targets.push(newTarget);
    selectedTargetToken.value = newTarget.token;
    debouncedPreview();
  };

  const selectTarget = (token) => (selectedTargetToken.value = token);

  // Auto-set translation fees when any target has multiple countries
  const applyTranslationFeesIfMultiCountry = () => {
    project.value.targets?.forEach((target) => {
      if (!target.countries?.length) return;

      if (target.countries.length > 1) {
        // Multi-country target: set default translation fees if not already set
        const baseFee = getTranslationManualBaseFee({ target });
        target.countries.forEach((country) => {
          if (country.translation_manual == null) {
            country.translation_manual = baseFee;
          }
        });
      }
    });
  };

  const initPreviewWatcher = () => {
    if (watcherInitialized.value) return;
    watcherInitialized.value = true;

    watch(
      () => project.value.targets,
      () => {
        if (skipNextWatch) {
          skipNextWatch = false;
          return;
        }
        if (project.value.token && !isPreviewLoading.value) {
          debouncedPreview();
        }
      },
      { deep: true },
    );

    // Watch for has_ask_licence changes and trigger preview
    watch(
      () => project.value.has_ask_licence,
      () => {
        if (project.value.token && !isPreviewLoading.value) {
          debouncedPreview();
        }
      },
    );

    // Watch for multi-country targets and auto-set translation fees
    watch(
      () => project.value.targets?.map((t) => t.countries?.length),
      applyTranslationFeesIfMultiCountry,
      { deep: true },
    );
  };

  return {
    // State
    project,
    initialProject,
    selectedTargetToken,

    // Getters
    isNewProject,
    designOptions,
    selectedTarget,
    hasProjectChanged,
    isAiProject,
    targetHasMissingParams,
    projectHasMissingParams,

    // Actions
    fetch,
    preview,
    createProject,
    updateProject,
    saveProject,
    resetProject,
    initProject,
    addTarget,
    removeTarget,
    duplicateTarget,
    selectTarget,
    initPreviewWatcher,
  };
});
